import { useEffect, useRef, useState } from 'react';
import { Button } from '@helios/ui';
import type { useVerifiedChanges } from '@/hooks/useVerifiedChanges';
import { VerifiedChangeIndicator } from './verified-change-indicator';
import { verifiedChangeMatchesRecord, type VerifiedChangeRecordIdentity } from './verified-change';

type Attention = Pick<
  ReturnType<typeof useVerifiedChanges>,
  'enabled' | 'scopeKey' | 'entries' | 'markSeen'
>;

/** Use the hook once per page, then pass its state beside the exact reviewed record. */
export function VerifiedChangeRecordCue({
  attention,
  record,
}: {
  attention: Attention;
  record: VerifiedChangeRecordIdentity;
}) {
  const presentation = attention.enabled
    ? attention.entries.find((entry) => verifiedChangeMatchesRecord(entry.change, record))
    : undefined;
  const eventId = presentation?.change.id;
  const receiptKey = eventId ? `${attention.scopeKey}:${eventId}` : null;
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<{ key: string; message: string } | null>(null);
  const currentEvent = useRef(receiptKey);
  currentEvent.current = receiptKey;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    setPendingId(null);
    setError(null);
  }, [receiptKey]);
  if (!presentation) return null;
  const pending = pendingId === receiptKey;
  const visibleError = error?.key === receiptKey ? error.message : null;
  const markSeen = async () => {
    if (pending || !eventId || !receiptKey) return;
    const submittedEvent = receiptKey;
    setPendingId(receiptKey);
    setError(null);
    try {
      await attention.markSeen(eventId);
    } catch (failure) {
      if (mounted.current && currentEvent.current === submittedEvent) {
        setError({
          key: submittedEvent,
          message: failure instanceof Error ? failure.message : 'Seen receipt unavailable',
        });
      }
    } finally {
      if (mounted.current && currentEvent.current === submittedEvent) setPendingId(null);
    }
  };
  return (
    <div
      className="flex flex-wrap items-center gap-2 text-xs"
      aria-label="Recorded change evidence"
    >
      <VerifiedChangeIndicator presentation={presentation} />
      {presentation.unseen ? (
        <Button
          type="button"
          size="xs"
          variant="ghost"
          disabled={pending}
          onClick={() => void markSeen()}
        >
          {pending ? 'Recording seen…' : visibleError ? 'Try again' : 'Mark seen'}
        </Button>
      ) : (
        <span className="text-muted-foreground">Seen</span>
      )}
      {visibleError ? (
        <span role="alert" className="text-muted-foreground">
          {visibleError}
        </span>
      ) : null}
    </div>
  );
}
